import { Component, inject, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import {MatMenu, MatMenuModule} from "@angular/material/menu";
import {MatBadge} from "@angular/material/badge";
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from "@angular/router";
import { filter } from "rxjs";
import { PermissionsService } from '../../domain/services/auth/permissions.service';
import { ModulePermissionServiceService } from '../../domain/services/module-permission/module-permissio.service';
import { AuthenticationService } from '../../domain/services/auth/authentication.service';
import { CashDrawerService } from '../../domain/services/cash-drawer/cash-drawer.service';
import { ModulePermission } from '../../domain/models/modulePermission/modulePermission';
import { NgModule } from '@angular/core';
@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.css',
  standalone: true,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule, 
    MatBadge,
    RouterLink,
    MatMenuModule,
    RouterLinkActive,
  ]
})
export class SideMenuComponent implements OnInit{
  pendingActionsCount: 13 | undefined;
  openedSection: string | null = null;

  activeMenu: string | null = null;
  visibleModules: any[] = [];

  // Maps API routeName values to actual Angular route paths
  private readonly routeMap: Record<string, string> = {
    'task': 'tasks',
    'search': 'search',
    'distributions': 'distributions',
    'settings': 'tasks', // Administrative has no direct route, handled by submenu
  };

  // Maps module names (when routeName is null) to Angular route paths
  private readonly nameToRouteMap: Record<string, string> = {
    'Reports': 'reports',
    'Dashboards': 'dashboard',
    'Correspondence': 'correspondence/emails',
  };

  // Maps admin sub-option names (when routeName is null) to Angular route paths
  private readonly adminOptionRouteMap: Record<string, string> = {
    'Distributions': 'distributions',
    'Holidays': 'admin/holidays',
    'Appraiser offices': 'admin/appraiser-offices',
    'Interest rates': 'admin/interest-rates',
    'Tax rates': 'admin/tax-rates',
    'Jurisdictions': 'admin/jurisdictions',
    'Exemption types': 'admin/exemption-types',
    'Fees': 'admin/fees',
    'Allowances': 'admin/allowances',
    'Global variables': 'admin/global-variables',
    'Return frecuencies': 'admin/return-frequencies',
    'Payment processor': 'admin/payment-processor',
  };

  // Admin sub-options injected client-side (not returned by the module API).
  private readonly virtualAdminOptions: any[] = [
    { moduleOptionId: 'v-admin-jurisdictions', name: 'Jurisdictions', routeName: null, hasPermissions: true },
    { moduleOptionId: 'v-admin-exemption-types', name: 'Exemption types', routeName: null, hasPermissions: true },
    { moduleOptionId: 'v-admin-appraiser-offices', name: 'Appraiser offices', routeName: null, hasPermissions: true },
    { moduleOptionId: 'v-admin-allowances', name: 'Allowances', routeName: null, hasPermissions: true },
    { moduleOptionId: 'v-admin-payment-processor', name: 'Payment processor', routeName: null, hasPermissions: true },
  ];

  // Fixed display order for admin sub-options
  private readonly adminOptionOrder: Record<string, number> = {
    'Groups': 1,
    'Users': 2,
    'Global variables': 3,
    'Fees': 5,
    'Allowances': 5.5,
    'Holidays': 6,
    'Appraiser offices': 6.5,
    'Payment offices': 7,
    'Interest rates': 8,
    'Return frecuencies': 9,
    'Tax rates': 11,
    'Distributions': 10,
    'Jurisdictions': 12,
    'Exemption types': 12.5,
    'Payment processor': 13,
  };

  /** Append client-side-only admin sub-options (e.g. Jurisdictions) to the Administrative module. */
  // Discovery arrives from the API as a bare module; its pages are front-end
  // routes, so the sub-menu is defined here rather than in the modules table.
  // Distributions arrives from the API as a bare module pointing at the schedule list. Its two
  // halves - running a distribution and maintaining the recurring schedules - are separate
  // pages, so they are given their own sub-menu here rather than in the modules table.
  private readonly distributionOptions = [
    { moduleOptionId: 'v-dist1', name: 'Run Distributions', routeName: 'distributions/runs' },
    // 'distributions' is a prefix of 'distributions/runs', so this one matches exactly
    // or both children would show as active on the runs page.
    { moduleOptionId: 'v-dist2', name: 'Scheduled Distributions', routeName: 'distributions', exactMatch: true },
  ];

  private readonly discoveryOptions = [
    { moduleOptionId: 'v-disc1', name: 'Cases', routeName: 'discovery' },
    { moduleOptionId: 'v-disc2', name: 'Map', routeName: 'discovery/map' },
    { moduleOptionId: 'v-disc3', name: 'County Parcels', routeName: 'discovery/parcels' },
    { moduleOptionId: 'v-disc4', name: 'Settings', routeName: 'discovery/settings' },
  ];

  private injectAdminExtras(modules: any[]): any[] {
    return modules.map(m => {
      if (m.name === 'Discovery') {
        // Options are granted per group (V62); show the granted ones with their front-end routes.
        // ponytail: a login cached before V62 carries no options at all -> full list until re-login.
        if (!m.optionCount) return { ...m, moduleOptions: this.discoveryOptions };
        const granted = m.moduleOptions || [];
        return { ...m, moduleOptions: this.discoveryOptions.filter(v => granted.some((o: any) => o.name === v.name)) };
      }
      if (m.name === 'Distributions') {
        const existing = m.moduleOptions || [];
        return existing.length ? m : { ...m, moduleOptions: this.distributionOptions };
      }
      if (m.name !== 'Administrative') { return m; }
      const existing = m.moduleOptions || [];
      const missing = this.virtualAdminOptions.filter(
        vo => !existing.some((o: any) => o.name === vo.name));
      return missing.length ? { ...m, moduleOptions: [...existing, ...missing] } : m;
    });
  }

  sortAdminOptions(options: any[]): any[] {
    return [...options].sort((a, b) => {
      const orderA = this.adminOptionOrder[a.name] ?? 99;
      const orderB = this.adminOptionOrder[b.name] ?? 99;
      return orderA - orderB;
    });
  }

  getAdminOptionRoute(option: any): string | null {
    if (option.routeName) {
      return this.routeMap[option.routeName] ?? option.routeName;
    }
    return this.adminOptionRouteMap[option.name] || null;
  }

  /**
   * Taking a payment is the job most of the day is spent on, so it sits at the very top of the
   * sidebar rather than a click deep inside a Cashering group. Cash Drawers is the only other
   * cashering screen and is now top-level too, which is why there is no Cashering group left.
   */
  private readonly leadingModules: any[] = [
    {
      id: 'v-take-payment',
      name: 'Take a Payment',
      icon: 'point_of_sale',
      hasPermission: true,
      routeName: 'cashering',
      // ponytail: exact, so this stops lighting up while you are on Cash Drawers (which lives
      // under /app/cashering too). Cost: deep pages like /app/cashering/:customerId highlight
      // nothing. Give the nav item a list of prefixes to match if that ever matters.
      exactMatch: true,
      moduleOptions: []
    }
  ];

  // Virtual modules not from the API
  private readonly virtualModules: any[] = [
    {
      id: 'v-cash-drawers',
      name: 'Cash Drawers',
      icon: 'account_balance_wallet',
      hasPermission: true,
      routeName: 'cashering/admin-drawers',
      moduleOptions: []
    },
    {
      id: 'v-correspondence',
      name: 'Correspondence',
      icon: 'mail',
      hasPermission: true,
      routeName: null,
      moduleOptions: [
        { moduleOptionId: 'v-c1', name: 'Compose Message', routeName: 'correspondence/compose' },
        { moduleOptionId: 'v-c3', name: 'Sent Messages', routeName: 'correspondence/emails' },
        { moduleOptionId: 'v-c4', name: 'Message Templates', routeName: 'correspondence/templates' },
        { moduleOptionId: 'v-c2', name: 'Automatic Notifications', routeName: 'admin/notification-templates' },
      ]
    },
    {
      id: 'v-query',
      name: 'Query',
      icon: 'manage_search',
      hasPermission: true,
      routeName: 'query',
      moduleOptions: []
    }
  ];

  // Modules that have expandable sub-menus
  isExpandableModule(module: any): boolean {
    return module.moduleOptions && module.moduleOptions.length > 0
      && (module.name === 'Administrative' || module.name === 'Correspondence'
        || module.name === 'Discovery' || module.name === 'Distributions');
  }

  // Get sorted options for a module
  getSortedOptions(module: any): any[] {
    if (module.name === 'Administrative') {
      return this.sortAdminOptions(module.moduleOptions);
    }
    return module.moduleOptions;
  }

  // Get route for a sub-option (works for both admin and correspondence)
  getOptionRoute(module: any, option: any): string | null {
    if (option.routeName) {
      return this.routeMap[option.routeName] ?? option.routeName;
    }
    if (module.name === 'Administrative') {
      return this.adminOptionRouteMap[option.name] || null;
    }
    return null;
  }

  getRouteForModule(routeName: string, moduleName?: string): string {
    if (moduleName && this.nameToRouteMap[moduleName]) {
      return this.nameToRouteMap[moduleName];
    }
    if (!routeName) {
      return moduleName ? (this.nameToRouteMap[moduleName] ?? 'tasks') : 'tasks';
    }
    return this.routeMap[routeName] ?? routeName;
  }

  toggleMenu(menu: string): void {
    this.activeMenu = this.activeMenu === menu ? null : menu;
  }

  /** True when the current route belongs to one of this module's sub-options. Drives the
   *  parent "active-trail" highlight (so a collapsed group still shows it owns the page)
   *  and the auto-expand on load. */
  isGroupActive(module: any): boolean {
    if (!this.isExpandableModule(module)) {
      return false;
    }
    const url = this.router.url;
    return this.getSortedOptions(module).some(option => {
      const route = this.getOptionRoute(module, option);
      return !!route && url.startsWith('/app/' + route);
    });
  }

  /** Open the group that owns the current route (only sets it once, doesn't fight the user). */
  private syncActiveGroup(): void {
    if (this.activeMenu) {
      return;
    }
    const active = this.visibleModules.find(m => this.isGroupActive(m));
    if (active) {
      this.activeMenu = active.name;
    }
  }

  private readonly drawers = inject(CashDrawerService);

  /** Take a Payment reads "Open Cash Drawer" until the cashier has a drawer open (QA 2026-09-30). */
  labelFor(module: any): string {
    return module.id === 'v-take-payment' && this.drawers.hasOpenDrawer() === false ? 'Open Cash Drawer' : module.name;
  }

  constructor(private auth: AuthenticationService, private moduleServies : ModulePermissionServiceService,
              private router: Router) {
    this.visibleModules = this.loadFromLocalStorage();
    this.syncActiveGroup();
    // Landing on a page from elsewhere (e.g. Create case on a task opens a discovery case)
    // should open the group that owns it, so its sub-item shows as selected.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      // A route can belong to two groups (Administrative > Distributions and the Distributions
      // group both own /app/distributions); keep the open one instead of jumping (QA 2026-10-04).
      const current = this.visibleModules.find(m => m.name === this.activeMenu);
      if (current && this.isGroupActive(current)) {
        return;
      }
      const active = this.visibleModules.find(m => this.isGroupActive(m));
      if (active) {
        this.activeMenu = active.name;
      }
    });
  }

  // Synchronous — runs at property init time, before first render
  private loadFromLocalStorage(): any[] {
    try {
      const cached = localStorage.getItem('appModules');
      if (cached) {
        const modules = JSON.parse(cached);
        if (Array.isArray(modules) && modules.length > 0) {
          return [...this.leadingModules,
                  ...this.injectAdminExtras(this.applyOptionPermissions(modules.filter((e: any) => e.hasPermission))),
                  ...this.virtualModules];
        }
      }
    } catch (e) {}
    // Return at least the virtual modules so Take a Payment/Correspondence/Query show immediately
    return [...this.leadingModules, ...this.virtualModules];
  }

  /** Hide sub-options the group is not granted. The sidebar already filters MODULES by
   *  hasPermission; this also honors the per-OPTION grant (enabled / viewable / editable,
   *  or a granted section) so a group only sees the screens it actually has. */
  private applyOptionPermissions(modules: any[]): any[] {
    return modules.map(m => ({
      ...m,
      optionCount: (m.moduleOptions || []).length,
      moduleOptions: (m.moduleOptions || []).filter((opt: any) =>
        opt.hasPermissions || opt.canView || opt.canEdit ||
        (opt.modulesOptionSections || []).some((s: any) =>
          s.hasPermission || s.hasPermissions || s.canView || s.canEdit)
      )
    }));
  }

  ngOnInit(): void {
    this.drawers.refreshOpenState();
    // If we already loaded from localStorage, skip API call
    if (this.visibleModules.length > this.leadingModules.length + this.virtualModules.length) {
      return;
    }
    // Fallback: fetch from API only if localStorage was empty
    const userGroupId = Number(localStorage.getItem('appUseGroup')) || 1;
    this.moduleServies.getAllbyUserGroup(userGroupId).subscribe(result => {
      this.visibleModules = [...this.leadingModules,
                             ...this.injectAdminExtras(this.applyOptionPermissions(result.filter(e => e.hasPermission))),
                             ...this.virtualModules];
      this.syncActiveGroup();
    });
  }
}
